// Lastslate editor schema for ProseMirror.
//
// Defines the screenplay document model: one root `doc`, seven block
// node types (scene_heading, action, character, dialogue, parenthetical,
// transition, shot) plus `text` for inline content, and three marks
// (strong, em, underline). This file is the load-bearing decision in
// the migration — once writers save documents with this schema, attrs
// are a migration to change.
//
// DOM contract: every block toDOM emits
//   <div class="<kebab-type>" data-block-type="<kebab-type>">…</div>
// matching the shape paginateBlocks / flushDomToScript / the CSS in
// renderer/css/app.css already expect. Node names use underscores
// (JS-safe property access); DOM class names use the existing
// kebab-case so pagination & styling work unchanged.
//
// `defining: true` on scene_heading and character so Backspace at the
// start lifts content into an action block instead of merging types.

import { Schema } from 'prosemirror-model';

// Every block carries a stable `bid` (UUIDv7) so future sync layers
// (Yjs CRDT, comment anchors, AI suggestion anchors) have an identifier
// that survives reshuffles. The attribute is rendered as `data-bid` so
// it round-trips through PM's own parseDOM/toDOM (used during copy-
// paste between editor instances). The DEFAULT is null; the
// appendTransaction backfill in `block-ids.js` stamps any node that
// lacks one. Generating IDs in the schema spec would force every
// `createAndFill` call site to either pre-build attrs or accept a
// re-stamped duplicate; the backfill keeps it lazy and idempotent.
const BID_ATTR = { bid: null };

function blockSpec({ cssClass, attrs = {}, defining = false }) {
  const mergedAttrs = { ...BID_ATTR, ...attrs };
  const attrSpec = {};
  for (const key of Object.keys(mergedAttrs)) {
    attrSpec[key] = { default: mergedAttrs[key] };
  }
  return {
    group: 'block',
    content: 'inline*',
    attrs: attrSpec,
    defining,
    parseDOM: [
      {
        tag: `div.${cssClass}`,
        getAttrs(dom) {
          const out = {};
          for (const key of Object.keys(mergedAttrs)) {
            const raw = dom.getAttribute(`data-${key.toLowerCase()}`);
            if (raw == null) {
              out[key] = mergedAttrs[key];
            } else if (raw === 'true' || raw === 'false') {
              out[key] = raw === 'true';
            } else if (raw !== '' && !Number.isNaN(Number(raw))) {
              out[key] = Number(raw);
            } else {
              out[key] = raw;
            }
          }
          return out;
        },
      },
    ],
    toDOM(node) {
      const domAttrs = { class: cssClass, 'data-block-type': cssClass };
      for (const key of Object.keys(mergedAttrs)) {
        const val = node.attrs[key];
        if (val !== null && val !== undefined && val !== mergedAttrs[key]) {
          domAttrs[`data-${key.toLowerCase()}`] = String(val);
        }
      }
      return ['div', domAttrs, 0];
    },
  };
}

export const schema = new Schema({
  nodes: {
    doc: {
      content: 'block+',
      attrs: {
        title: { default: null },
        titlePage: { default: null },
      },
    },

    // Action is listed first among the block group so PM's content-match
    // fillBefore picks it as the default block when the user deletes
    // everything (Cmd-A → Delete). The previous order made scene_heading
    // the implicit fallback, which produced the "INT./EXT. LOCATION -
    // TIME" placeholder for a freshly-cleared doc — the wrong nudge
    // since most writers want to open on action / FADE IN: / BLACK.
    action: blockSpec({
      cssClass: 'action',
      attrs: { forced: false },
    }),

    scene_heading: blockSpec({
      cssClass: 'scene-heading',
      attrs: { sceneNumber: null, forced: false },
      defining: true,
    }),

    character: blockSpec({
      cssClass: 'character',
      attrs: { dual: false, extension: null, forced: false },
      defining: true,
    }),

    parenthetical: blockSpec({
      cssClass: 'parenthetical',
    }),

    dialogue: blockSpec({
      cssClass: 'dialogue',
    }),

    transition: blockSpec({
      cssClass: 'transition',
      attrs: { forced: false },
    }),

    shot: blockSpec({
      cssClass: 'shot',
    }),

    centered: blockSpec({
      cssClass: 'centered',
    }),

    text: { group: 'inline' },

    // Soft line break inside a block (Shift-Enter). Renders as a <br>;
    // unlike Enter (which splits the block / inserts a new one), this
    // keeps the writer inside the same paragraph type.
    hard_break: {
      inline: true,
      group: 'inline',
      selectable: false,
      parseDOM: [{ tag: 'br' }],
      toDOM: () => ['br'],
    },
  },

  marks: {
    strong: {
      parseDOM: [{ tag: 'strong' }, { tag: 'b' }],
      toDOM: () => ['strong', 0],
    },
    em: {
      parseDOM: [{ tag: 'em' }, { tag: 'i' }],
      toDOM: () => ['em', 0],
    },
    underline: {
      parseDOM: [{ tag: 'u' }, { style: 'text-decoration=underline' }],
      toDOM: () => ['u', 0],
    },
  },
});
